import { Button, ButtonSize, Intent, Shape } from '@axieinfinity/matcha'
import { LinkBreakIcon, WalletIcon } from '@axieinfinity/matcha-icons'
import { format } from 'date-fns'
import type { FC } from 'react'
import React from 'react'

import {
  WalletUnlinkModal,
  WalletUnLockedModal,
  WalletWrongAddressModal,
} from '~/components/Socials/Ronin/RoninModal'
import { useModal } from '~/hooks/useModal'

import type { TConnectProps } from '../Connect'
import PreConnectWalletModal from './RoninModal/PreConnectWalletModal'
import WalletLinkErrorModal from './RoninModal/WalletLinkError'
import WalletLinkModal from './RoninModal/WalletLinkModal'
import useHooks, { ErrorRoninWallet } from './useHooks'

const Ronin: FC<TConnectProps> = props => {
  const { connected, className, disabled = false } = props
  const {
    isLoading,
    blockedTime,
    handleConnect,
    errorRonin,
    setErrorRonin,
    universalLink,
    isVisiblePreConnectModal,
    resetPreConnectWallet,
  } = useHooks(props)
  const {
    isVisible: isVisibleUnlink,
    openModal: openModalUnlink,
    closeModal: closeModalUnlink,
  } = useModal()
  const {
    isVisible: isVisibleLink,
    openModal: openModalLink,
    closeModal: closeModalLink,
  } = useModal()

  return (
    <>
      {connected ? (
        <Button
          icon={LinkBreakIcon}
          className={className}
          size={ButtonSize.Small}
          text="Unlink"
          shape={Shape.Default}
          onClick={openModalUnlink}
          disabled={disabled}
        />
      ) : (
        <Button
          icon={WalletIcon}
          className={className}
          size={ButtonSize.Small}
          text="Link Ronin Wallet"
          shape={Shape.Default}
          intent={Intent.Primary}
          onClick={openModalLink}
          disabled={disabled}
        />
      )}

      <WalletUnLockedModal
        visible={errorRonin === ErrorRoninWallet.UNLOCKED}
        onClose={() => setErrorRonin(ErrorRoninWallet.EMPTY)}
      />
      <WalletWrongAddressModal
        visible={errorRonin === ErrorRoninWallet.WRONG_ADDRESS}
        onClose={() => setErrorRonin(ErrorRoninWallet.EMPTY)}
      />
      <WalletLinkErrorModal
        visible={errorRonin === ErrorRoninWallet.LINK}
        title="Wallet cannot be linked"
        content={
          <div className="mb-8 text-body-m text-text-color-subdued">
            {`This wallet has been unlinked recently. You can't link this
            wallet to another account `}
            <span className="font-bold text-text-color-default">
              for 30 days
            </span>
            {` from the date you unlink. Please try again on `}
            {format(new Date(+blockedTime * 1000), 'MMM dd YYY, hh:mm aa')}.
          </div>
        }
        onClose={() => setErrorRonin(ErrorRoninWallet.EMPTY)}
      />
      <WalletLinkErrorModal
        visible={errorRonin === ErrorRoninWallet.UNLINK}
        title="Wallet cannot be unlinked"
        content={
          <div className="mb-8 text-body-m text-text-color-subdued">
            {`You've recently unlinked a wallet from this account. You need
            to wait `}
            <span className="font-bold text-text-color-default">
              for 30 days
            </span>
            {` to unlink another wallet. Please try again on `}
            {format(new Date(+blockedTime * 1000), 'MMM dd YYY, hh:mm aa')}.
          </div>
        }
        onClose={() => setErrorRonin(ErrorRoninWallet.EMPTY)}
      />

      <WalletUnlinkModal
        disabled={isLoading}
        visible={isVisibleUnlink && !errorRonin}
        onConfirm={async () => {
          await handleConnect()
          closeModalUnlink()
        }}
        onClose={closeModalUnlink}
      />
      <WalletLinkModal
        disabled={isLoading}
        visible={isVisibleLink}
        onConfirm={async () => {
          await handleConnect()
          closeModalLink()
        }}
        onClose={closeModalLink}
      />
      <PreConnectWalletModal
        visible={isVisiblePreConnectModal}
        universalLink={universalLink}
        onClose={() => {
          resetPreConnectWallet()
          closeModalLink()
        }}
      />
    </>
  )
}

export default Ronin
